import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Receipt, Download, Trash2, Pencil } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => Number(v).toFixed(2).replace(".", ",");

const CLIENTE_FIELDS = [
  ["denominazione", "Intestazione (ragione sociale)", "col-span-2"],
  ["indirizzo", "Indirizzo", "col-span-2"],
  ["cap", "CAP", ""], ["citta", "Città", ""], ["provincia", "Provincia (sigla)", ""],
  ["partita_iva", "Partita IVA", ""], ["codice_fiscale", "Codice fiscale", ""], ["codice_sdi", "Codice SDI", ""],
  ["pec", "PEC", ""], ["email_fattura", "Email per la ricevuta", ""],
];

export function ClienteDialog({ token, ricevuta, onClose, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(CLIENTE_FIELDS.map(([k]) => [k, ricevuta.cliente?.[k] || ""])));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.denominazione.trim()) return toast.error("Inserisci l'intestazione");
    setSaving(true);
    try {
      await axios.put(`${API}/api/super-admin/ricevute/${ricevuta.id}/cliente`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Dati cliente salvati sulla ricevuta n. ${ricevuta.numero}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ricevuta-cliente-dialog">
        <DialogHeader>
          <DialogTitle>Dati cliente — ricevuta n. {ricevuta.numero}</DialogTitle>
          <DialogDescription>Compaiono nel PDF della ricevuta e vengono salvati anche come dati fattura dell'azienda per le prossime ricevute.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {CLIENTE_FIELDS.map(([k, label, cls]) => (
            <div key={k} className={cls}>
              <label className="text-xs text-slate-500">{label}</label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`cliente-${k}-input`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cliente-salva-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

async function openPdf(token, id, numero) {
  const res = await axios.get(`${API}/api/super-admin/ricevute/${id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = `Ricevuta_${numero}.pdf`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const CANONE_V2 = { "Pro Light": () => 4.9, Pro: (n) => n * 1.5, Enterprise: () => 99 };

function presetsFor(company, dateStr) {
  const now = dateStr ? new Date(dateStr) : new Date();
  const mese = `${MESI[now.getMonth()]} ${now.getFullYear()}`;
  const n = company?.num_dipendenti ?? 0;
  const calc = CANONE_V2[company?.piano_nome];
  const canone = calc ? Math.round(calc(n) * 100) / 100 : "";
  return [
    { key: "canone", label: "Canone abbonamento", descrizione: `Canone mensile abbonamento app Ferie e Permessi — piano ${company?.piano_nome || ""}${calc && company?.piano_nome === "Pro" ? ` (${n} dipendenti)` : ""} — ${mese}`, netto: canone },
    { key: "gettone", label: "Gettone personalizzazione", descrizione: "Personalizzazione dell'applicazione software secondo le specifiche richieste dal cliente.", netto: "" },
    { key: "altro", label: "Altro (scrivi tu)", descrizione: "", netto: "" },
  ];
}

export function RicevutaDialog({ token, company, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [preset, setPreset] = useState("canone");
  const [form, setForm] = useState({
    descrizione: presetsFor(company, today)[0].descrizione, netto: presetsFor(company, today)[0].netto,
    condizioni_pagamento: "Carta di Credito su Stripe",
    data: today,
  });
  const presets = presetsFor(company, form.data);
  const applyPreset = (key) => {
    const p = presets.find((x) => x.key === key);
    setPreset(key);
    setForm({ ...form, descrizione: p.descrizione, netto: p.netto });
  };
  const changeData = (data) => {
    const p = presetsFor(company, data).find((x) => x.key === preset);
    setForm({ ...form, data, descrizione: preset === "canone" ? p.descrizione : form.descrizione });
  };
  const [saving, setSaving] = useState(false);
  const netto = parseFloat(String(form.netto).replace(",", ".")) || 0;
  const lordo = Math.round((netto / 0.8) * 100) / 100;
  const ritenuta = Math.round((lordo - netto) * 100) / 100;
  const noBilling = !company?.billing_info?.denominazione;

  const submit = async () => {
    if (netto <= 0) return toast.error("Inserisci l'importo netto pagato dal cliente");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/super-admin/ricevute`,
        { azienda_id: company.id, ...form, netto },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Ricevuta n. ${res.data.numero} emessa`);
      await openPdf(token, res.data.id, res.data.numero);
      onCreated && onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'emissione");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ricevuta-dialog">
        <DialogHeader>
          <DialogTitle>Nuova ricevuta — {company?.nome_azienda}</DialogTitle>
          <DialogDescription>Ritenuta d'acconto 20%: inserisci il netto incassato, il lordo si calcola da solo.</DialogDescription>
        </DialogHeader>
        {noBilling && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="ricevuta-no-billing-warning">
            Questa azienda non ha ancora compilato i "Dati per la fattura": la ricevuta riporterà solo il nome azienda.
          </p>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Tipo di ricevuta</label>
            <select value={preset} onChange={(e) => applyPreset(e.target.value)} className="w-full border border-slate-200 rounded-md h-10 px-3 text-sm bg-white" data-testid="ricevuta-preset-select">
              {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Descrizione attività (modificabile)</label>
            <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="ricevuta-descrizione-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Netto incassato (€)</label>
              <Input value={form.netto} onChange={(e) => setForm({ ...form, netto: e.target.value })} placeholder="99,00" data-testid="ricevuta-netto-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Data</label>
              <Input type="date" value={form.data} onChange={(e) => changeData(e.target.value)} data-testid="ricevuta-data-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Condizioni di pagamento</label>
            <Input value={form.condizioni_pagamento} onChange={(e) => setForm({ ...form, condizioni_pagamento: e.target.value })} data-testid="ricevuta-pagamento-input" />
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1" data-testid="ricevuta-anteprima">
            <div className="flex justify-between"><span>Compenso lordo</span><strong>€ {eur(lordo)}</strong></div>
            <div className="flex justify-between"><span>Ritenuta d'acconto 20%</span><strong>€ {eur(ritenuta)}</strong></div>
            <div className="flex justify-between text-blue-700"><span>Netto pagato</span><strong>€ {eur(netto)}</strong></div>
            {lordo > 77.47 && <p className="text-xs text-slate-500 pt-1">Verrà indicata la marca da bollo da € 2,00 sull'originale.</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Annulla</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ricevuta-emetti-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Receipt className="h-4 w-4 mr-2" />}Emetti e scarica PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RicevuteList({ token, refreshKey }) {
  const [items, setItems] = useState([]);
  const [editCliente, setEditCliente] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/super-admin/ricevute`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.ricevute || [])).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (r) => {
    if (!window.confirm(`Annullare la ricevuta n. ${r.numero}? Possibile solo per l'ultima emessa.`)) return;
    try {
      await axios.delete(`${API}/api/super-admin/ricevute/${r.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ricevuta annullata"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="mt-8" data-testid="ricevute-list-card">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-600" /> Ricevute emesse ({items.length})</CardTitle></CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna ricevuta emessa dall'app. La numerazione parte da 26001.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Num.</th><th>Data</th><th>Azienda</th><th className="text-right">Lordo</th><th className="text-right">Ritenuta</th><th className="text-right">Netto</th><th></th></tr></thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`ricevuta-row-${r.numero}`}>
                  <td className="py-2 font-semibold">{r.numero}</td>
                  <td>{new Date(r.data).toLocaleDateString("it-IT")}</td>
                  <td>{r.cliente?.denominazione || r.nome_azienda}</td>
                  <td className="text-right">€ {eur(r.importi.lordo)}</td>
                  <td className="text-right">€ {eur(r.importi.ritenuta)}</td>
                  <td className="text-right font-semibold">€ {eur(r.importi.netto)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setEditCliente(r)} className={`p-1 ${r.cliente?.indirizzo && r.cliente?.partita_iva ? "text-slate-500 hover:text-slate-700" : "text-amber-600 hover:text-amber-800"}`} title={r.cliente?.indirizzo && r.cliente?.partita_iva ? "Modifica dati cliente" : "Dati cliente incompleti — clicca per inserirli"} data-testid={`ricevuta-cliente-${r.numero}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => openPdf(token, r.id, r.numero)} className="text-blue-600 hover:text-blue-800 p-1" title="Scarica PDF" data-testid={`ricevuta-pdf-${r.numero}`}><Download className="h-4 w-4" /></button>
                    {i === 0 && <button onClick={() => remove(r)} className="text-red-500 hover:text-red-700 p-1" title="Annulla ultima ricevuta" data-testid={`ricevuta-delete-${r.numero}`}><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {editCliente && <ClienteDialog token={token} ricevuta={editCliente} onClose={() => setEditCliente(null)} onSaved={load} />}
      </CardContent>
    </Card>
  );
}
